import { useEffect, useMemo, useRef, useState } from "react";
import { BookOpen, LayoutGrid, Search, Sparkles, Upload, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export type KmComposerPlusMenuView = "root" | "libraries";

type TabId = "general" | "libraries";

export type KmLibraryScopeOption = {
  id: string;
  name: string;
  selected: boolean;
};

type KmComposerPlusMenuProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** View to show when the menu opens (e.g. Manage scope → libraries). */
  initialView?: KmComposerPlusMenuView;
  disabled?: boolean;
  showLibrariesTab?: boolean;
  libraries?: KmLibraryScopeOption[];
  onLibraryIdsChange?: (ids: string[]) => void;
  onAttachDocs?: () => void;
  agenticAvailable?: boolean;
  agenticEnabled?: boolean;
  onAgenticChange?: (enabled: boolean) => void;
};

function matchesSearch(name: string, query: string) {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return true;
  return name.toLowerCase().includes(normalized);
}

function MenuSwitch({
  checked,
  disabled,
  onCheckedChange,
  label,
}: {
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: (next: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={(event) => {
        event.stopPropagation();
        onCheckedChange(!checked);
      }}
      className={cn(
        "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors",
        checked ? "bg-violet-500" : "bg-[#cbd5e1] dark:bg-slate-600",
        disabled && "cursor-not-allowed opacity-50",
      )}
    >
      <span
        className={cn(
          "pointer-events-none absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform",
          checked ? "left-4" : "left-0.5",
        )}
      />
    </button>
  );
}

export function KmComposerPlusMenu({
  open,
  onOpenChange,
  initialView = "root",
  disabled = false,
  showLibrariesTab = false,
  libraries = [],
  onLibraryIdsChange,
  onAttachDocs,
  agenticAvailable = false,
  agenticEnabled = false,
  onAgenticChange,
}: KmComposerPlusMenuProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [tab, setTab] = useState<TabId>("general");
  const [searchQuery, setSearchQuery] = useState("");

  const showAgentic = Boolean(agenticAvailable && onAgenticChange);
  const attachDisabled = disabled || !onAttachDocs;

  useEffect(() => {
    if (!open) return;
    const preferLibraries = initialView === "libraries" && showLibrariesTab;
    setTab(preferLibraries ? "libraries" : "general");
    setSearchQuery("");
    // Only re-init when opening / changing initialView — not when parent props
    // update after auto-apply (that would reset tab/search mid-session).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
  }, [open, initialView, showLibrariesTab]);

  useEffect(() => {
    if (!showLibrariesTab && tab === "libraries") {
      setTab("general");
    }
  }, [showLibrariesTab, tab]);

  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: MouseEvent) {
      const target = event.target as Node | null;
      if (!target) return;
      if (panelRef.current?.contains(target)) return;
      if ((target as Element).closest?.("[data-composer-plus-trigger]")) return;
      onOpenChange(false);
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onOpenChange(false);
    }

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onOpenChange]);

  const filteredLibraries = useMemo(
    () => libraries.filter((library) => matchesSearch(library.name, searchQuery)),
    [libraries, searchQuery],
  );

  const selectedCount = libraries.filter((library) => library.selected).length;

  function handleToggleLibrary(libraryId: string, nextChecked: boolean) {
    if (!onLibraryIdsChange) return;
    const currentIds = libraries.filter((library) => library.selected).map((library) => library.id);
    let nextIds: string[];
    if (nextChecked) {
      nextIds = currentIds.includes(libraryId) ? currentIds : [...currentIds, libraryId];
    } else {
      if (currentIds.length <= 1 && currentIds.includes(libraryId)) return;
      nextIds = currentIds.filter((id) => id !== libraryId);
    }
    if (nextIds.length === 0) return;
    onLibraryIdsChange(nextIds);
  }

  function handleClose() {
    onOpenChange(false);
  }

  if (!open) return null;

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Add to chat"
      className="absolute bottom-full left-0 z-30 mb-2 flex w-[22rem] max-w-[calc(100vw-2rem)] max-h-[min(28rem,70vh)] flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#111b35] text-white shadow-xl"
    >
      <div className="shrink-0 border-b border-white/10 px-4 pb-0 pt-3.5">
        <div className="mb-3 flex items-start justify-between gap-2">
          <h2 className="text-sm font-semibold text-white">Add to chat</h2>
          <button
            type="button"
            aria-label="Close"
            onClick={handleClose}
            className="rounded-md p-0.5 text-slate-400 transition-colors hover:bg-white/5 hover:text-white"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex gap-4" role="tablist" aria-label="Add to chat sections">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "general"}
            onClick={() => setTab("general")}
            className={cn(
              "relative flex items-center gap-1.5 pb-2.5 text-sm font-medium transition-colors",
              tab === "general" ? "text-white" : "text-slate-400 hover:text-slate-200",
            )}
          >
            <LayoutGrid className="h-3.5 w-3.5" />
            General
            {tab === "general" ? (
              <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-violet-500" />
            ) : null}
          </button>
          {showLibrariesTab ? (
            <button
              type="button"
              role="tab"
              aria-selected={tab === "libraries"}
              onClick={() => {
                setTab("libraries");
                setSearchQuery("");
              }}
              className={cn(
                "relative flex items-center gap-1.5 pb-2.5 text-sm font-medium transition-colors",
                tab === "libraries" ? "text-white" : "text-slate-400 hover:text-slate-200",
              )}
            >
              <BookOpen className="h-3.5 w-3.5" />
              Libraries
              {tab === "libraries" ? (
                <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-violet-500" />
              ) : null}
            </button>
          ) : null}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto px-4 py-3">
        {tab === "general" ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2.5">
              <button
                type="button"
                disabled={attachDisabled}
                onClick={() => {
                  onAttachDocs?.();
                  onOpenChange(false);
                }}
                className="flex flex-col items-start gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-3 text-left transition-colors hover:bg-white/10 disabled:opacity-50"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 text-slate-300">
                  <Upload className="h-4 w-4" />
                </span>
                <span>
                  <span className="block text-sm font-medium text-white">Upload files</span>
                  <span className="block text-xs text-slate-400">PDF, DOCX, and more</span>
                </span>
              </button>
            </div>

            {showAgentic ? (
              <div className="flex w-full items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-3 py-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/10 text-slate-300">
                  <Sparkles className="h-4 w-4" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-white">Agentic</span>
                  <span className="block text-xs text-slate-400">
                    {agenticEnabled ? "On" : "Off"}
                  </span>
                </span>
                <MenuSwitch
                  checked={Boolean(agenticEnabled)}
                  disabled={disabled}
                  label="Agentic search"
                  onCheckedChange={(enabled) => onAgenticChange?.(enabled)}
                />
              </div>
            ) : null}
          </div>
        ) : null}

        {tab === "libraries" && showLibrariesTab ? (
          <div className="flex flex-col">
            <div className="relative mb-2">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-500" />
              <Input
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search libraries"
                className="h-8 border-white/10 bg-white/5 pl-8 text-sm text-white placeholder:text-slate-500"
                aria-label="Search libraries"
              />
            </div>
            <p className="mb-1 text-[11px] text-slate-500">
              {selectedCount} selected · at least one required
            </p>
            <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
              {filteredLibraries.length === 0 ? (
                <p className="px-1 py-6 text-center text-sm text-slate-500">
                  {libraries.length === 0 ? "No libraries in this workspace" : "No matching libraries"}
                </p>
              ) : (
                <ul>
                  {filteredLibraries.map((library) => {
                    const isLastSelected = library.selected && selectedCount <= 1;
                    return (
                      <li
                        key={library.id}
                        className="flex min-w-0 w-full items-center gap-2 rounded-lg px-1 py-1 hover:bg-white/5"
                      >
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-white/10 text-slate-300">
                          <BookOpen className="h-3.5 w-3.5" />
                        </span>
                        <span className="min-w-0 flex-1 truncate text-sm font-medium text-white">
                          {library.name}
                        </span>
                        <MenuSwitch
                          checked={library.selected}
                          disabled={disabled || !onLibraryIdsChange || isLastSelected}
                          label={`Include ${library.name}`}
                          onCheckedChange={(next) => handleToggleLibrary(library.id, next)}
                        />
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
